import { AfterContentChecked, Component, HostListener, OnDestroy, OnInit, ViewChild, NgZone, ChangeDetectorRef  } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { MsalService } from '@azure/msal-angular';
import { AvatarModule } from 'primeng/avatar';
import { TabMenuModule } from 'primeng/tabmenu';
import { DropdownModule } from 'primeng/dropdown';
import { MenubarModule } from 'primeng/menubar';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { MenuModule } from 'primeng/menu';
import { BadgeModule } from 'primeng/badge';
import { MenuItem, MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { StateService } from '../../../core/services/state.service';
import { AuthService } from '../../../core/auth/auth.service';
import { ButtonModule } from 'primeng/button';
import { Road } from '../../../modules/select-road/models/select-road.model';
import { StorageService } from '../../../core/services/storage.service';
import { ADMIN_NAV_ITEMS, NAV_ITEMS } from '../../constants/navigation-items';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CommunicationsComponent } from '../../../modules/admin/communications/communications.component';
import { AlertAudioService } from '../../../core/services/alert-audio.service';
import { AlertsWebSocketService } from '../../../modules/center-activity/services/alerts-websocket.service'; 
import { WebSocketDiagnostics, WebSocketDiagnosticStatus } from '../../../modules/center-activity/services/alerts-websocket.service';
import { map, Subject, Subscription, takeUntil } from 'rxjs';
import { RoadInfoPanelComponent } from '../road-context/road-info-panel.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    MenubarModule,
    AvatarModule,
    DropdownModule,
    FormsModule,
    TabMenuModule,
    ButtonModule,
    MenuModule,
    BadgeModule,
    OverlayPanelModule,
    CommonModule,
    RoadInfoPanelComponent
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements AfterContentChecked, OnInit, OnDestroy {

  private readonly _destroying$ = new Subject<void>();
  
  @ViewChild('notificationsStatePanel') notificationsStatePanel?: OverlayPanel;

  @ViewChild('roadInfoPanel') roadInfoOverlay!: RoadInfoPanelComponent;

  navItems: MenuItem[] | undefined;

  adminNavItems: MenuItem[] | undefined;

  activeNavItem: MenuItem | undefined;

  logoutOptions = [{ label: 'Cerrar sesión', value: 'logout' }];

  logoutOptionSelected: string | null = null;

  userName: string | undefined = '';

  avatarName: string | undefined = '';

  roads: Road[] = [];
  selectedRoad: Road | null = null;
  userRoles: string[] = [];

  private websocketSubscription: Subscription | null = null;
  private websocketDiagnosticsSubscription: Subscription | null = null;
  private availableRoadsSubscription: Subscription | null = null;
  private selectedRoadSubscription: Subscription | null = null;
  private refreshRoadsSubscription: Subscription | null = null;

  ref: DynamicDialogRef | undefined;

  hasUnreadAlertEvents = false; //flag que alerta o no de notificaciones del websocket
  isNotificationsMuted = false; //flag para activar/desactivar el sonido de la notificación

  // Para almacenar el titulo de la página en el TAB del navegador
  private originalTitle = '';

  notifications: any[] = [];
  websocketDiagnostics: WebSocketDiagnostics | null = null;

  constructor(
    private authService: MsalService,
    private authServiceRoles: AuthService,
    private router: Router,
    private stateService: StateService,
    private storageService: StorageService,
    public dialogService: DialogService,
    private alertAudioService: AlertAudioService,
    private messageService: MessageService,
    private webSocketService: AlertsWebSocketService,
    private titleService: Title,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  isAdmin$ = this.authServiceRoles.userRoles$.pipe(
    map(roles => roles.includes('admin'))
  );

  isCoordinator$ = this.authServiceRoles.userRoles$.pipe(
    map(roles => roles.includes('coordinador'))
  );

  private refreshRoads(roads: Road[]): void {
    this.roads = roads;
    this.refreshSelectedRoad(roads);
  }

  private refreshSelectedRoad(roads: Road[]): void {
    const currentSelectedRoad = this.stateService.getCurrentSelectedRoad();

    if (!currentSelectedRoad) return;

    const updatedSelectedRoad = roads.find((road) => road.id === currentSelectedRoad.id);

    this.stateService.setSelectedRoad(updatedSelectedRoad || null);
  }

  /**
   * Listener para detectar cuando el usuario cierra la pestaña o el navegador, 
   * para cerrar la conexión WebSocket y evitar que quede abierta sin necesidad
   */
  @HostListener('window:unload')
  handleUnload() {
    // Cierre de la conexión WebSocket al cerrar la pestaña o el navegador
    this.disconnectWebSocket();
  }

  ngOnInit(): void {

    // Guardamos el título original de la página para poder restaurarlo después
    this.originalTitle = this.titleService.getTitle();

    this.initNavItems();
    this.initAdminNavItems();
    this.subscribeToWebSocketDiagnostics();

    // Suscripción al evento disparado para obtener las carreteras disponibles para su selección
    this.availableRoadsSubscription = this.stateService.availableRoads$.subscribe((roads) => (this.roads = roads));

    // Suscripción al evento que se dispara al seleccionar un nueva carretera
    this.selectedRoadSubscription = this.stateService.selectedRoad$.subscribe((road) => {
      if (road && road !== this.selectedRoad) {
        this.selectedRoad = road;
        
        // Desconectar WebSocket anterior si existía
        this.disconnectWebSocket();

        // Conectar al WebSocket de la nueva carretera seleccionada
        this.connectWebSocket();

      }
    });

    // Suscripción al evento disparado cuando se refresca el listado de carreteras disponibles
    this.refreshRoadsSubscription = this.stateService.refreshRoads$.subscribe((roads) => {
      this.refreshRoads(roads);
    });

    this.authServiceRoles.userRoles$
      .pipe(takeUntil(this._destroying$))
      .subscribe((roles) => {
        this.userRoles = roles;
      });
  }

  ngAfterContentChecked(): void {
    this.userName = this.authService.instance.getActiveAccount()?.name;
    this.avatarName = this.authService.instance.getActiveAccount()?.username.substring(0, 2);
  }

  ngOnDestroy(): void {
    // Cerrar conexión WebSocket al destruir el componente 
    this.disconnectWebSocket();
    // Cancelar suscripción a diagnósticos del WebSocket
    this.websocketDiagnosticsSubscription?.unsubscribe();
    this.availableRoadsSubscription?.unsubscribe();
    this.selectedRoadSubscription?.unsubscribe();
    this.refreshRoadsSubscription?.unsubscribe();
    this._destroying$.next();
    this._destroying$.complete();
  }

  onActiveItemChange(event: MenuItem) {
    this.activeNavItem = event;
  }

  accessRoad() {
    if (this.selectedRoad) {
      // Cerrar conexión websocket anterior si existía
      this.disconnectWebSocket();

      this.stateService.setSelectedRoad(this.selectedRoad);

      // Abrir nueva conexión al websocket para la carretera seleccionada
      this.connectWebSocket();

      // Solo redirigir si no estamos ya en la ruta de center-activity
      if (!this.router.url.includes('center-activity')) {
        this.router.navigate(['/center-activity']);
      }
    }
  }

  private initNavItems() {
    this.navItems = NAV_ITEMS;
  }

  private initAdminNavItems() {
    // Pictogramas y comunicaciones solo acceso si es 'admin'
    this.authServiceRoles.userRoles$
    .pipe(takeUntil(this._destroying$))
    .subscribe((roles) => {
      const isAdmin = roles.includes('admin');

      this.adminNavItems = ADMIN_NAV_ITEMS.filter(
        (item) => isAdmin || item.label !== 'Pictogramas'
      );

      if (isAdmin) {
        this.adminNavItems.push({
          label: 'Comunicaciones',
          command: () => this.openCommunications(),
        });
      }
    });
  }

  private openCommunications() {
    this.ref = this.dialogService.open(CommunicationsComponent, {
      header: 'Configuración de Comunicaciones',

      closable: false,
    });

    this.ref.onClose.subscribe((res) => {
      if (res) {
        console.log(res);
      }
    });
  }

  /**
   * Método que gestiona la recepción de un nuevo evento en el websocket
   */
  newAlertEventReceived(event: any) {

    // Cambiamos el título de la página para indicar que hay un nuevo evento
    this.titleService.setTitle(`(*) ${this.originalTitle}`);

    // Añadimos a la campana el icono de nuevo evento
    this.hasUnreadAlertEvents = true;

    // Añadimos el evento a la lista de notificaciones
    this.notifications.push({type: event.type, message: event.message, context: event.context, timestamp: new Date().toLocaleString()});
    
    // Reproducimos sonido de alerta, si no tenemos el mute marcado
    if(!this.isNotificationsMuted) this.alertAudioService.playAlert();

    // Si estamos en la pantalla de centro actividad, lanzamos evento de refresco de datos
    if (this.router.url.includes('center-activity')) {
      // Refrescamos los datos de la pantalla
      this.stateService.triggerCenterActivityRefresh();
    }

    // Mostramos notificación de evento recibido
    if(document.visibilityState !== 'visible') return;

    // Mostramos notificación de evento recibido
    this.messageService.add({ 
      severity: 'warn', 
      summary: '¡Atención!', 
      detail: 'Nuevo evento recibido en ' + this.selectedRoad?.text  + ': ' + event.message,
      life: 10000
    });
    
  }

  /**
   * Método que gestiona la navegación al centro de actividad al hacer click en la campana de alertas
   */
  navigateToAlerts() {
    if(this.hasUnreadAlertEvents) {

      // Restauramos el título original de la página
      this.titleService.setTitle(this.originalTitle);

      // Quitamos de la campana el icono de nuevo evento
      this.hasUnreadAlertEvents = false;

      // Limpiamos la lista de notificaciones
      this.clearNotifications();

      // Navegar al centro de actividad
      if (!this.router.url.includes('center-activity')) {
        this.router.navigate(['/center-activity']);
      }
    }

  }

  /**
   * Método para marcar las notificaciones del websocket como leídas, limpiando la lista
   */
  clearNotifications(): void {
    // Restauramos el título original de la página
    this.titleService.setTitle(this.originalTitle);

    // Quitamos de la campana el icono de nuevo evento
    this.hasUnreadAlertEvents = false;

    // Limpiamos la lista de notificaciones
    this.notifications = [];
  }

  /**
   * Método para activar/desactivar el sonido de las notificaciones del websocket
   */
  muteNotifactions(): void {
    // cambiamos el estado que tuviera el flag para activar o desactivar 
    // la notificación sonora
    this.isNotificationsMuted = !this.isNotificationsMuted;
  }

  /**
   * Método para reconectar el WebSocket manualmente desde la interfaz 
   */
  reconnectWebSocket(): void {
    if (!this.selectedRoad) return;

    // Ocultamos el panel de notificaciones
    this.notificationsStatePanel?.hide();
    // Desconectamos el WebSocket actual
    this.disconnectWebSocket();
    // Conectamos al WebSocket de la carretera seleccionada
    this.connectWebSocket();

    // Si estamos en la pantalla de centro actividad, lanzamos evento de refresco de datos
    if (this.router.url.includes('center-activity')) {
      // Refrescamos los datos de la pantalla
      this.stateService.triggerCenterActivityRefresh();
    }
  }

  /**
   * Métoodo para obtener una etiqueta legible del estado del WebSocket 
   * a partir del status recibido en los diagnósticos
   * @param status 
   * @returns etiqueta legible del estado
   */
  getWebSocketStatusLabel(status?: WebSocketDiagnosticStatus | null): string {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'connecting':
        return 'Conectando';
      case 'reconnecting':
        return 'Reconectando';
      default:
        return 'Desconectado';
    }
  }

  /**
   * Conecta el WebSocket para recibir notificaciones de cambios en alertas
   */
  private connectWebSocket(): void {
    if (!this.selectedRoad) return;

    // Conectar el WebSocket
    this.webSocketService.connect(this.selectedRoad.id, this.selectedRoad.text);

    // Suscribirse a los mensajes del WebSocket
    this.websocketSubscription = this.webSocketService.messages$.subscribe((message: any) => {
      console.log('Notificación de cambio en alertas recibida:', message);

      // Disparamos el evento de nueva alerta recibida
      this.newAlertEventReceived(message);
    });
  }

  /**
   * Desconexión del WebSocket
   */
  private disconnectWebSocket(): void {
    this.webSocketService.disconnect();
    if (this.websocketSubscription) {
      this.websocketSubscription.unsubscribe();
      this.websocketSubscription = null;
    }
  }

  /**
   * Método para la suscripción a los diagnósticos del WebSocket
   */
  private subscribeToWebSocketDiagnostics(): void {
    this.websocketDiagnosticsSubscription = this.webSocketService.diagnostics$.subscribe(
      (diagnostics) => {
        this.ngZone.run(() => {
          this.websocketDiagnostics = diagnostics;
          this.cdr.detectChanges();
        });
      }
    );
  }

  openNewTab() {
    // Obtener la URL base de la aplicación
    const baseUrl = window.location.origin;
    window.open(baseUrl, '_blank');
  }

  logout() {
    if (this.logoutOptionSelected) {
      this.disconnectWebSocket();
      this.storageService.removeAllSessionStorage();
      this.authService.logoutRedirect();
    }
  }

  openRoadInfoPanel(event: Event) {
    this.roadInfoOverlay.open(event);
  }
}
